import { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { Heart, Sparkles, ArrowRight, ImageIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

// A very quiet leaf-shadow overlay — mimics dappled morning light spilling through a window.
// Deliberately minimal, single corner, low opacity — reads as ambient texture, not decoration.
const LeafShadowOverlay = ({ style }) => (
  <svg viewBox="0 0 400 500" preserveAspectRatio="xMinYMin slice" style={style} aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <filter id="soft-blur" x="-20%" y="-20%" width="140%" height="140%">
        <feGaussianBlur stdDeviation="6" />
      </filter>
    </defs>
    <g filter="url(#soft-blur)" fill="#8C7A5C" opacity="0.28">
      {/* Long willow-like leaf shadows */}
      <ellipse cx="80" cy="60" rx="90" ry="14" transform="rotate(35 80 60)"/>
      <ellipse cx="120" cy="110" rx="110" ry="12" transform="rotate(28 120 110)"/>
      <ellipse cx="60" cy="180" rx="80" ry="10" transform="rotate(45 60 180)"/>
      <ellipse cx="140" cy="230" rx="120" ry="14" transform="rotate(32 140 230)"/>
      <ellipse cx="70" cy="320" rx="90" ry="12" transform="rotate(42 70 320)"/>
      <ellipse cx="180" cy="380" rx="100" ry="11" transform="rotate(38 180 380)"/>
      {/* Little leaf clusters */}
      <ellipse cx="220" cy="70" rx="18" ry="8" transform="rotate(25 220 70)"/>
      <ellipse cx="260" cy="150" rx="16" ry="7" transform="rotate(30 260 150)"/>
      <ellipse cx="200" cy="280" rx="20" ry="9" transform="rotate(40 200 280)"/>
    </g>
  </svg>
);


export default function MeetGenesis() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/about').then(({ data }) => {
      setData(data);
      // Warm the browser cache — start downloading every gallery photo + hero
      // immediately, so they're ready by the time the visitor scrolls to them.
      const urls = [];
      if (data.hero_photo_url) urls.push(data.hero_photo_url);
      (data.gallery || []).forEach(p => urls.push(p.url));
      urls.forEach(u => { const img = new Image(); img.src = u; });
    }).finally(() => setLoading(false));
  }, []);

  const paragraphs = (data?.story || '').split(/\n{2,}/).filter(Boolean);

  return (
    <div
      style={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        // Homepage cream base
        backgroundColor: '#FDFBF7',
      }}
    >
      <Header />

      {/* Real photographic background — warm cream paper/linen texture, kept very quiet.
          Reads as ambient warmth, not a hero photo. */}
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 0,
          pointerEvents: 'none',
          backgroundImage: "url('https://images.unsplash.com/photo-1585687501004-615dedb33f8f?auto=format&fit=crop&w=1600&q=80')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          opacity: 0.55,
          filter: 'saturate(0.7) brightness(1.1)',
        }}
      />
      {/* Cream wash on top — keeps the page airy like the homepage while letting the texture whisper through */}
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 0,
          pointerEvents: 'none',
          background: 'linear-gradient(180deg, rgba(253, 251, 247, 0.78) 0%, rgba(253, 251, 247, 0.82) 60%, rgba(253, 251, 247, 0.9) 100%)',
        }}
      />
      {/* One very gentle warm-light glow — like morning sun spilling in from the top-right */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: -80,
          right: -120,
          width: 620,
          height: 620,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(232, 194, 101, 0.15) 0%, transparent 65%)',
          filter: 'blur(30px)',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />

      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Hero */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 pb-8" data-testid="meet-genesis-hero">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-1.5 rounded-lg" style={{ backgroundColor: '#E57A4415' }}>
              <Heart className="w-4 h-4" style={{ color: '#E57A44' }} strokeWidth={2.5} />
            </div>
            <span className="text-[11px] font-bold uppercase tracking-widest" style={{ fontFamily: 'Manrope, sans-serif', color: '#E57A44' }}>
              Meet Genesis
            </span>
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl mb-6 leading-[1.1]"
            style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724', fontWeight: 600 }}
            data-testid="meet-genesis-title"
          >
            The heart behind<br />Love Restored
          </h1>

          {/* Hero photo */}
          {/* Hero photo — any orientation displays in full (no cropping). Soft cream padding fills any extra space. */}
          <div
            className="relative w-full rounded-3xl overflow-hidden mb-8 flex items-center justify-center"
            style={{
              minHeight: 320,
              maxHeight: '75vh',
              backgroundColor: '#F3EFE7',
              boxShadow: '0 20px 50px rgba(44, 39, 36, 0.12)',
              border: '8px solid #FFFFFF',
            }}
            data-testid="meet-genesis-hero-photo"
          >
            {data?.hero_photo_url ? (
              <img
                src={data.hero_photo_url}
                alt="Genesis"
                className="meet-fade-in"
                style={{
                  maxWidth: '100%',
                  maxHeight: '75vh',
                  width: 'auto',
                  height: 'auto',
                  display: 'block',
                }}
                fetchpriority="high"
              />
            ) : (
              <div className="w-full aspect-[16/10] flex flex-col items-center justify-center gap-2" style={{ color: '#B8AF9D' }}>
                <ImageIcon className="w-12 h-12" strokeWidth={1.5} />
                <span className="text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>Hero photo coming soon</span>
              </div>
            )}
          </div>
        </section>

        {/* Story */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-14" data-testid="meet-genesis-story">
          {loading ? (
            <div className="space-y-3">
              {[1,2,3].map(i => <div key={i} className="h-4 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.4)' }} />)}
            </div>
          ) : (
            <div style={{ fontFamily: 'Lora, serif' }}>
              {paragraphs.map((p, i) => (
                <p key={i} className="mb-5 leading-[1.85]" style={{ fontSize: '17px', color: '#2C2724' }}>
                  {p}
                </p>
              ))}
            </div>
          )}
        </section>

        {/* Fun / masonry gallery section */}
        <section className="py-14 sm:py-20 relative" data-testid="meet-genesis-fun-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="max-w-2xl mb-10">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4" style={{ color: '#B8891A' }} strokeWidth={2.5} />
                <span className="text-[11px] font-bold uppercase tracking-widest" style={{ fontFamily: 'Manrope, sans-serif', color: '#8B6913' }}>
                  A little glimpse
                </span>
              </div>
              <h2
                className="text-3xl sm:text-4xl mb-3"
                style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724', fontWeight: 600 }}
                data-testid="meet-genesis-fun-heading"
              >
                {data?.fun_heading || 'A few things I love'}
              </h2>
              <p
                className="leading-relaxed"
                style={{ fontFamily: 'Manrope, sans-serif', color: '#5C4B33', fontSize: '16px' }}
                data-testid="meet-genesis-fun-intro"
              >
                {data?.fun_intro || ''}
              </p>
            </div>

            {(!data?.gallery || data.gallery.length === 0) ? (
              <div
                className="rounded-3xl p-10 text-center"
                style={{
                  backgroundColor: 'rgba(255, 253, 245, 0.7)',
                  border: '2px dashed rgba(140, 122, 92, 0.35)',
                  color: '#8C7A5C',
                  fontFamily: 'Manrope, sans-serif',
                  backdropFilter: 'blur(4px)',
                }}
                data-testid="meet-genesis-gallery-empty"
              >
                <ImageIcon className="w-8 h-8 mx-auto mb-2" strokeWidth={1.5} />
                <p className="text-sm">Photos coming soon.</p>
              </div>
            ) : (
              <div
                className="masonry-gallery"
                data-testid="meet-genesis-gallery"
                style={{ columnCount: 2, columnGap: '16px' }}
              >
                {data.gallery.map((p) => (
                  <figure
                    key={p.id}
                    className="masonry-item mb-4 relative rounded-2xl overflow-hidden"
                    style={{
                      breakInside: 'avoid',
                      display: 'inline-block',
                      width: '100%',
                      boxShadow: '0 8px 24px rgba(44, 39, 36, 0.10)',
                      border: '6px solid #FFFFFF',
                    }}
                    data-testid={`meet-genesis-photo-${p.id}`}
                  >
                    <img
                      src={p.url}
                      alt={p.caption || 'Genesis'}
                      className="meet-fade-in"
                      style={{ width: '100%', height: 'auto', display: 'block' }}
                    />
                    {p.caption && (
                      <figcaption
                        className="absolute inset-x-0 bottom-0 p-3 text-white text-xs sm:text-sm"
                        style={{
                          fontFamily: 'Manrope, sans-serif',
                          background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.75) 100%)',
                          fontWeight: 500,
                        }}
                      >
                        {p.caption}
                      </figcaption>
                    )}
                  </figure>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Sanctuary CTA */}
        <section className="py-14 sm:py-20" data-testid="meet-genesis-cta">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div
              className="rounded-3xl p-8 sm:p-12 text-center relative overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, rgba(245, 237, 221, 0.95) 0%, rgba(232, 200, 155, 0.9) 100%)',
                border: '1px solid rgba(184, 137, 26, 0.15)',
                boxShadow: '0 24px 60px rgba(78, 60, 40, 0.20)',
                backdropFilter: 'blur(8px)',
              }}
            >
              <Sparkles className="w-8 h-8 mx-auto mb-3" style={{ color: '#8B6913' }} strokeWidth={2} />
              <h3 className="text-2xl sm:text-3xl mb-3" style={{ fontFamily: 'Playfair Display, serif', color: '#3D2E0A', fontWeight: 600 }}>
                Want to walk this journey with me?
              </h3>
              <p className="mb-6 leading-relaxed max-w-lg mx-auto" style={{ fontFamily: 'Manrope, sans-serif', color: '#5C4515' }}>
                Step inside The Sanctuary — a private prayer wall, articles inspired by your requests, and a soft place to land.
              </p>
              <Link
                to="/sanctuary"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full transition-all duration-300 hover:-translate-y-0.5"
                style={{ fontFamily: 'Manrope, sans-serif', background: 'linear-gradient(135deg, #2C2724 0%, #4A3F35 100%)', color: '#F5EDDD', fontWeight: 700, boxShadow: '0 6px 20px rgba(44, 39, 36, 0.30)' }}
                data-testid="meet-genesis-sanctuary-cta"
              >
                Take a Look <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
              </Link>
            </div>
          </div>
        </section>

        <style>{`
          .meet-fade-in {
            animation: meetFadeIn 0.5s ease-out both;
          }
          @keyframes meetFadeIn {
            from { opacity: 0; transform: scale(0.985); }
            to { opacity: 1; transform: scale(1); }
          }
          @media (min-width: 640px) {
            .masonry-gallery { column-count: 3 !important; column-gap: 20px !important; }
            .masonry-item { margin-bottom: 20px !important; }
          }
          @media (min-width: 1024px) {
            .masonry-gallery { column-count: 4 !important; }
          }
        `}</style>
      </div>

      <Footer />
    </div>
  );
}
